import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Dimmer,
  Icon,
  Loader,
  Message,
  Progress,
  Segment,
  SemanticICONS,
} from 'semantic-ui-react';
import { searchContent } from '@plone/volto/actions';
import { RenderBlocks } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import './decisionaid.less';

type NodeOption = {
  label: string;
  next: string;
  icon: string;
};

type Node = {
  id: string;
  label: string;
  text?: string;
  options?: NodeOption[];
  uids?: string[];
};

type DecisionTree = {
  initialNode: string;
  leafText: string;
  depth: number;
  nodes: Node[];
};

type State = {
  search: {
    items: Object[];
    loading: boolean;
    data: Object;
  };
};

type Object = {
  [key: string]: string;
};

interface Props {
  data: {
    title: string;
    icon?: string;
    description: {
      data: string;
    };
    cta_text?: string;
    decision_tree: string;
  };
  isEditMode: boolean;
}

const View: React.FC<Props> = ({ data }) => {
  /* the uids of the final recommendations */
  const [uids, setUids] = useState<string[]>([]);
  const [history, setHistory] = useState<Node[]>([]);

  const isLoading = useSelector(
    (state: State) => state.search?.loading || false,
  );
  const items = useSelector((state: State) => state.search?.items).filter(
    (item) => uids.indexOf(item.UID) > -1,
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (uids.length > 0) {
      dispatch(
        searchContent('/', {
          UID: uids,
          fullobjects: true,
        }),
      );
    }
  }, [dispatch, uids]);

  let decisionTree: DecisionTree = {
    initialNode: '',
    leafText: '',
    depth: 0,
    nodes: [],
  };

  const setCurNodeById = (nodeId: string) => {
    const node = decisionTree.nodes.find((n) => n.id === nodeId) || null;
    if (node) {
      setHistory((prev) => [...prev, node]);
      if (node.uids) setUids(node.uids);
    }
  };

  const goBack = () => {
    setHistory((prev) => prev.slice(0, -1));
  };

  useEffect(() => {
    setCurNodeById(decisionTree.initialNode);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  try {
    decisionTree = JSON.parse(data.decision_tree);
  } catch (err) {
    let message = 'Unknown error while parsing JSON';
    if (err instanceof Error) {
      message = err.message;
    }
    return <Message negative>{message}</Message>;
  }

  const curNode = history?.length > 0 ? history[history.length - 1] : false;
  if (!curNode) return <></>;

  return (
    // @ts-ignore
    <Segment className="decision-aid">
      {data.title?.length > 0 && (
        <h2>
          {data.icon && <Icon name={data.icon as SemanticICONS} />}
          {data.title}
        </h2>
      )}
      {data.description?.data?.length > 0 && history.length < 2 && (
        <div
          style={{ marginBottom: '1rem' }}
          dangerouslySetInnerHTML={{
            __html: data.description.data,
          }}
        />
      )}
      {curNode.text && <div style={{ margin: '1rem 0' }}>{curNode.text}</div>}
      {(curNode.options || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {curNode.options?.map((option, index) => (
            <Button
              className="cta-button"
              color="red"
              key={`btn-option-${index}`}
              onClick={() => setCurNodeById(option.next)}
              icon={option.icon ? true : false}
            >
              {option.icon && <Icon name={option.icon as SemanticICONS} />}
              {option.label}
            </Button>
          ))}
        </div>
      )}
      {(curNode.uids || []).length > 0 && (
        <>
          {isLoading ? (
            <Dimmer active inverted>
              <Loader />
            </Dimmer>
          ) : items.length === 0 ? (
            <Message warning>
              Es gab einen Fehler. Bitte wenden Sie sich an{' '}
              <a
                href={`mailto:support@uni-koblenz.de?subject=Fehler in der MFA-Entscheidungshilfe&body=Empfehlungen für diese Auswahl konnten nicht geladen werden: ${history
                  .map((n) => n.label)
                  .join(' > ')}`}
              >
                support@uni-koblenz.de
              </a>
              .
            </Message>
          ) : (
            <>
              <p>{decisionTree.leafText}</p>
              {items.map((item, index) => (
                <div className="recommendation-content">
                  <RenderBlocks
                    blocksConfig={config.blocks.blocksConfig}
                    content={item}
                  />
                  <a
                    href={flattenToAppURL(item['@id'])}
                    target="_blank"
                    rel="noreferrer"
                    className="link-to-full-page"
                  >
                    <Button icon size="mini" labelPosition="right">
                      <FormattedMessage
                        id="Open single view"
                        defaultMessage="Open single view"
                      />
                      <Icon name="external alternate" />
                    </Button>
                  </a>
                </div>
              ))}
            </>
          )}
        </>
      )}
      {history.length > 1 && (
        <div>
          <Button size="mini" onClick={goBack} icon labelPosition="left">
            <Icon name="arrow left" />
            Zurück
          </Button>
        </div>
      )}
      <Progress
        percent={Math.floor(((history.length - 1) / decisionTree.depth) * 100)}
        color="red"
        size="tiny"
      />
    </Segment>
  );
};

export default View;
